import { useEffect, useRef, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { MdFolder, MdPhotoLibrary } from "react-icons/md";
import { useTranslation } from "../context/TranslationContext";
import DsIcon from "./ds/Icon";

/**
 * The "+" beside a chat's box: File, or Photos and videos — WhatsApp's attach menu, with
 * just those two. Either opens the device's picker, several at a time; `onPick(files)`.
 * `inField` is for the small chat, where it sits at the right end of the message field.
 */
const ChatAttachMenu = ({ onPick, isDark, disabled = false, compact = false, inField = false, className = "" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const fileInput = useRef(null);
  const mediaInput = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (input) => {
    setOpen(false);
    input.current?.click();
  };
  const picked = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) onPick(files);
  };

  const items = [
    { key: "file", label: t("chatFiles.file"), Icon: MdFolder, color: "text-sky-400", input: fileInput },
    { key: "media", label: t("chatFiles.photosAndVideos"), Icon: MdPhotoLibrary, color: "text-indigo-400", input: mediaInput },
  ];

  const panel = "border-separator-default bg-bg-raised text-label-primary";
  const circle = isDark ? "bg-white/[0.08] group-hover:bg-white/[0.14]" : "bg-slate-100 group-hover:bg-slate-200";

  return (
    <div className={`relative shrink-0 ${className}`} ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-label={t("chatFiles.attach")}
        title={t("chatFiles.attach")}
        aria-expanded={open}
        className={`grid place-items-center transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
          inField ? "rounded-full" : "rounded-radius-sm"
        } ${compact ? "h-8 w-8" : "h-11 w-11"} ${open ? (isDark ? "bg-white/20" : "bg-slate-200") : "hover:bg-fill-quaternary"}`}
      >
        {inField ? (
          <DsIcon name="image" size={18} />
        ) : (
          <FiPlus
            className={`${compact ? "h-4 w-4" : "h-5 w-5"} transition-transform duration-200 ${open ? "rotate-45" : ""} ${
              "text-label-primary"
            }`}
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute bottom-full ${inField ? "right-0" : "left-0"} z-50 mb-3 flex rounded-radius-md border shadow-2xl ${panel} ${
            compact ? "gap-1 p-2" : "gap-2 p-3"
          }`}
        >
          {items.map(({ key, label, Icon, color, input }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              onClick={() => choose(input)}
              className={`group flex flex-col items-center gap-1.5 rounded-radius-sm text-center ${compact ? "w-[76px] p-1" : "w-24 p-1.5"}`}
            >
              <span className={`grid place-items-center rounded-full transition ${circle} ${compact ? "h-12 w-12" : "h-16 w-16"}`}>
                <Icon className={`${color} ${compact ? "h-6 w-6" : "h-7 w-7"}`} />
              </span>
              <span className={`font-medium leading-tight ${compact ? "text-caption-1" : "text-footnote"}`}>{label}</span>
            </button>
          ))}
          <span
            aria-hidden
            className={`absolute -bottom-[7px] h-3 w-3 rotate-45 border-b border-r ${panel} ${inField ? "right-[10px]" : compact ? "left-[10px]" : "left-[16px]"}`}
          />
        </div>
      )}

      <input ref={fileInput} type="file" multiple className="hidden" onChange={picked} tabIndex={-1} />
      <input ref={mediaInput} type="file" multiple accept="image/*,video/*" className="hidden" onChange={picked} tabIndex={-1} />
    </div>
  );
};

export default ChatAttachMenu;
